import type { JoinIntent } from "@xiuxian/game-protocol";

/** URL query keys that drive the room entry flow. */
export const INVITE_CODE_PARAM = "room";
export const CREATE_ROOM_PARAM = "createRoom";

const INVITE_CODE_MIN_LENGTH = 4;
const INVITE_CODE_MAX_LENGTH = 12;

/** Trims, uppercases and strips non-alphanumerics from a typed invite code. */
export function normalizeInviteCodeInput(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "")
    .slice(0, INVITE_CODE_MAX_LENGTH);
}

export function isValidInviteCode(value: string): boolean {
  const normalized = normalizeInviteCodeInput(value);
  return (
    normalized.length >= INVITE_CODE_MIN_LENGTH &&
    normalized.length <= INVITE_CODE_MAX_LENGTH
  );
}

function isTruthyFlag(value: string | null): boolean {
  return value !== null && value !== "0" && value.toLowerCase() !== "false";
}

/**
 * Derives the join intent from a URL query string:
 * - `?room=ABCD12` -> join that private room
 * - `?createRoom=1` -> open the create-private-room flow
 * - otherwise -> official quick play
 */
export function parseJoinIntentFromSearch(search: string): JoinIntent {
  const params = new URLSearchParams(search);
  const room = params.get(INVITE_CODE_PARAM);
  if (room !== null && isValidInviteCode(room)) {
    return { kind: "private_join", inviteCode: normalizeInviteCodeInput(room) };
  }
  if (isTruthyFlag(params.get(CREATE_ROOM_PARAM))) {
    return { kind: "private_create" };
  }
  return { kind: "official" };
}

/** Builds a shareable invite link for the given code. */
export function buildInviteUrl(
  inviteCode: string,
  base: { origin: string; pathname: string },
): string {
  const url = new URL(base.pathname, base.origin);
  url.searchParams.set(INVITE_CODE_PARAM, normalizeInviteCodeInput(inviteCode));
  return url.toString();
}
